import React, { useEffect, useMemo, useState } from 'react';
import CakeOutlinedIcon from '@mui/icons-material/CakeOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useBirthday } from '../../context/BirthdayContext';

const confettiPalette = ['#1d4ed8', '#0f766e', '#f59e0b', '#dc2626', '#7c3aed', '#0ea5e9'];
const monthOptions = [
	{ value: '01', label: 'Jan' },
	{ value: '02', label: 'Feb' },
	{ value: '03', label: 'Mar' },
	{ value: '04', label: 'Apr' },
	{ value: '05', label: 'May' },
	{ value: '06', label: 'Jun' },
	{ value: '07', label: 'Jul' },
	{ value: '08', label: 'Aug' },
	{ value: '09', label: 'Sep' },
	{ value: '10', label: 'Oct' },
	{ value: '11', label: 'Nov' },
	{ value: '12', label: 'Dec' },
];

const parseDobParts = (dob) => {
	if (!dob || typeof dob !== 'string') {
		return { month: '', day: '', year: '' };
	}

	const [year = '', month = '', day = ''] = dob.split('-');
	return { month, day, year };
};

const buildDobValue = ({ month, day, year }) => {
	if (!month || !day || !year || year.length !== 4) {
		return '';
	}

	const parsedDay = Number(day);
	if (!Number.isInteger(parsedDay) || parsedDay < 1 || parsedDay > 31) {
		return '';
	}

	return `${year}-${month}-${String(parsedDay).padStart(2, '0')}`;
};

const BirthdayCelebrationOverlay = ({ open, onClose, name }) => {
	if (!open) return null;

	return (
		<div className="fixed inset-0 z-[140] flex items-start justify-center px-4 pt-12 pointer-events-none">
			<div className="birthday-confetti" aria-hidden>
				{Array.from({ length: 18 }).map((_, index) => (
					<span
						key={index}
						className="birthday-confetti-piece"
						style={{
							left: `${(index * 5.2) + (index % 3) * 2}%`,
							backgroundColor: confettiPalette[index % confettiPalette.length],
							animationDelay: `${(index % 6) * 0.18}s`,
							animationDuration: `${4.8 + (index % 4) * 0.4}s`,
						}}
					/>
				))}
			</div>

			<div className="pointer-events-auto relative w-full max-w-xl overflow-hidden rounded-[32px] border border-slate-200 bg-[linear-gradient(135deg,#ffffff_0%,#f8fbff_52%,#fffdf7_100%)] shadow-[0_30px_120px_rgba(15,23,42,0.22)] backdrop-blur-xl">
				<div className="pointer-events-none absolute inset-0" aria-hidden>
					<div className="absolute -left-6 -top-10 h-40 w-40 rounded-full bg-[radial-gradient(circle,rgba(59,130,246,0.12),transparent_68%)]" />
					<div className="absolute -right-12 -top-14 h-44 w-44 rounded-full bg-[radial-gradient(circle,rgba(245,158,11,0.12),transparent_70%)]" />
				</div>
				<button
					type="button"
					onClick={onClose}
					className="absolute right-6 top-6 rounded-full border border-slate-200 bg-white/90 p-2 text-slate-500 transition hover:border-slate-300 hover:text-slate-900"
				>
					<CloseRoundedIcon fontSize="small" />
				</button>
				<div className="relative px-10 py-10 text-center">
					<div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-[linear-gradient(135deg,#eff6ff_0%,#ffffff_55%,#fff7ed_100%)] shadow-[0_18px_38px_rgba(37,99,235,0.14)]">
						<div className="relative h-12 w-12">
							<span className="absolute left-2 top-0 h-7 w-7 rounded-full bg-[#e6c48c]" />
							<span className="absolute left-6 top-1 h-8 w-8 rounded-full bg-[#c3cfad]" />
							<span className="absolute left-0 top-5 h-6 w-6 rounded-full bg-[#ece7d3]" />
							<span className="absolute left-[14px] top-6 h-6 w-px bg-[#d5c7ad]" />
							<span className="absolute left-[30px] top-8 h-5 w-px bg-[#c3cfad]" />
							<span className="absolute left-[7px] top-9 h-4 w-px bg-[#ded7c5]" />
						</div>
					</div>
					<h2 className="mt-6 text-[40px] font-semibold tracking-[-0.04em] leading-[0.95] text-slate-900">Happy Birthday, {name}</h2>
					<p className="mt-4 text-lg text-slate-700">Wishing you a strong year ahead.</p>
					<p className="mt-5 text-base text-slate-600">The team is celebrating with you today.</p>
				</div>
			</div>
		</div>
	);
};

const BirthdayDobPromptModal = ({ open, currentUser, serverToday, onSave, onDefer }) => {
	const [dobParts, setDobParts] = useState(parseDobParts(currentUser?.dob || ''));
	const [showReminderChoices, setShowReminderChoices] = useState(false);
	const [selectedReminderDays, setSelectedReminderDays] = useState(null);
	const [submitting, setSubmitting] = useState(false);
	const currentYear = useMemo(() => {
		return serverToday?.slice(0, 4) || '9999';
	}, [serverToday]);
	const dob = useMemo(() => buildDobValue(dobParts), [dobParts]);

	useEffect(() => {
		if (open) {
			setDobParts(parseDobParts(currentUser?.dob || ''));
			setShowReminderChoices(false);
			setSelectedReminderDays(null);
		}
	}, [currentUser?.dob, open]);

	if (!open) return null;

	const handleSave = async () => {
		setSubmitting(true);
		try {
			if (showReminderChoices) {
				if (!selectedReminderDays) return;
				await onDefer(selectedReminderDays);
				return;
			}

			if (!dob) return;
			await onSave(dob);
		} finally {
			setSubmitting(false);
		}
	};

	const handleDefer = async (days) => {
		setSubmitting(true);
		try {
			await onDefer(days);
		} finally {
			setSubmitting(false);
		}
	};

	const isSaveDisabled = showReminderChoices
		? !selectedReminderDays || submitting
		: !dob || submitting;

	return (
		<div className="fixed inset-0 z-[150] flex items-center justify-center bg-slate-950/45 px-4 py-8 backdrop-blur-sm">
			<div className="relative w-full max-w-md overflow-hidden rounded-[24px] border border-rose-200 bg-[linear-gradient(135deg,#fff1f6_0%,#fff7ed_48%,#ffffff_100%)] p-6 shadow-[0_28px_100px_rgba(15,23,42,0.20)]">
				<div className="pointer-events-none absolute inset-x-0 top-0 h-28 bg-[radial-gradient(circle_at_top_left,rgba(244,63,94,0.22),transparent_50%),radial-gradient(circle_at_top_right,rgba(249,115,22,0.18),transparent_38%)]" />
				<div className="flex items-start gap-4">
					<span className="mt-1 flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-rose-100 via-white to-amber-100 text-rose-600 shadow-sm ring-1 ring-rose-200">
						<CakeOutlinedIcon />
					</span>
					<div className="flex-1">
						<h2 className="text-2xl font-semibold text-slate-900">Add your birthday</h2>
						<p className="mt-1 text-sm text-slate-600">A day worth celebrating.</p>
					</div>
				</div>

				{!showReminderChoices && (
					<>
						<div className="mt-8 pt-3 grid grid-cols-12 gap-3">
							<div className="relative col-span-5">
								<CalendarMonthOutlinedIcon className="!absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" fontSize="small" />
								<select
									value={dobParts.month}
									onChange={(event) => setDobParts((prev) => ({ ...prev, month: event.target.value }))}
									className="w-full appearance-none rounded-xl border border-rose-200 bg-white/95 py-3 pl-10 pr-4 text-sm text-slate-900 outline-none transition focus:border-rose-400 focus:ring-2 focus:ring-rose-100"
								>
									<option value="">Month</option>
									{monthOptions.map((month) => (
										<option key={month.value} value={month.value}>
											{month.label}
										</option>
									))}
								</select>
							</div>
							<div className="col-span-3">
								<input
									type="text"
									inputMode="numeric"
									maxLength={2}
									value={dobParts.day}
									onChange={(event) => {
										const nextValue = event.target.value.replace(/\D/g, '').slice(0, 2);
										setDobParts((prev) => ({ ...prev, day: nextValue }));
									}}
									placeholder="Day"
									className="w-full rounded-xl border border-rose-200 bg-white/95 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-rose-400 focus:ring-2 focus:ring-rose-100"
								/>
							</div>
							<div className="col-span-4">
								<input
									type="text"
									inputMode="numeric"
									maxLength={4}
									value={dobParts.year}
									onChange={(event) => {
										const nextValue = event.target.value.replace(/\D/g, '').slice(0, 4);
										setDobParts((prev) => ({ ...prev, year: nextValue }));
									}}
									placeholder="YYYY"
									className="w-full rounded-xl border border-rose-200 bg-white/95 px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-rose-400 focus:ring-2 focus:ring-rose-100"
								/>
							</div>
						</div>
						<p className="mt-2 text-xs text-slate-500">
							Year must be 4 digits and cannot be after {currentYear}.
						</p>
					</>
				)}

				{showReminderChoices && (
					<div className="mt-8 pt-3">
						<div className="grid grid-cols-2 gap-3">
							<button
								type="button"
								disabled={submitting}
								onClick={() => setSelectedReminderDays(7)}
								className={`rounded-xl border px-4 py-3 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${
									selectedReminderDays === 7
										? 'border-blue-600 bg-blue-50 text-blue-700'
										: 'border-slate-200 bg-white/90 text-slate-700 hover:border-slate-300'
								}`}
							>
								7 days
							</button>
							<button
								type="button"
								disabled={submitting}
								onClick={() => setSelectedReminderDays(14)}
								className={`rounded-xl border px-4 py-3 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${
									selectedReminderDays === 14
										? 'border-blue-600 bg-blue-50 text-blue-700'
										: 'border-slate-200 bg-white/90 text-slate-700 hover:border-slate-300'
								}`}
							>
								14 days
							</button>
						</div>
					</div>
				)}

				<div className="mt-6 flex items-center justify-between gap-3">
					<div>
						{!showReminderChoices && (
							<button
								type="button"
								disabled={submitting}
								onClick={() => setShowReminderChoices(true)}
								className="rounded-xl border border-rose-200 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-rose-300 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
							>
								Skip
							</button>
						)}
						{showReminderChoices && (
							<button
								type="button"
								disabled={submitting}
								onClick={() => {
									setShowReminderChoices(false);
									setSelectedReminderDays(null);
								}}
								className="rounded-xl border border-rose-200 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-rose-300 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
							>
								Back
							</button>
						)}
					</div>
					<button
						type="button"
						disabled={isSaveDisabled}
						onClick={handleSave}
						className={`min-w-[96px] rounded-xl border px-5 py-3 text-sm font-semibold transition ${
							isSaveDisabled
								? 'cursor-not-allowed border-slate-300 bg-slate-200 text-slate-600'
								: 'border-blue-600 bg-blue-600 text-white shadow-sm hover:bg-blue-700'
						}`}
					>
						{submitting ? 'Saving...' : 'Save'}
					</button>
				</div>
			</div>
		</div>
	);
};

const BirthdayExperienceManager = () => {
	const {
		birthdayData,
		saveDob,
		deferDobPrompt,
	} = useBirthday();

	const currentUser = birthdayData?.current_user;
	const shouldShowDobPrompt = birthdayData?.dob_prompt?.should_show === true;
	const [showPrompt, setShowPrompt] = useState(false);
	const [showCelebration, setShowCelebration] = useState(false);

	useEffect(() => {
		setShowPrompt(shouldShowDobPrompt);
	}, [shouldShowDobPrompt]);

	useEffect(() => {
		if (!currentUser?.user_id || currentUser?.is_birthday !== true) {
			return undefined;
		}

			const todayKey = new Date().toISOString().slice(0, 10);
			const appToday = birthdayData?.server_today || todayKey;
			const storageKey = `birthday-celebration-${currentUser.user_id}-${appToday}`;

		if (sessionStorage.getItem(storageKey)) {
			return undefined;
		}

		sessionStorage.setItem(storageKey, 'shown');
		setShowCelebration(true);

		const timeout = setTimeout(() => {
			setShowCelebration(false);
		}, 9000);

		return () => clearTimeout(timeout);
	}, [birthdayData?.server_today, currentUser?.is_birthday, currentUser?.user_id]);

	if (!birthdayData) {
		return null;
	}

	return (
		<>
				<BirthdayDobPromptModal
					open={showPrompt}
					currentUser={currentUser}
					serverToday={birthdayData?.server_today}
					onSave={async (dob) => {
						await saveDob(dob);
						setShowPrompt(false);
				}}
				onDefer={async (days) => {
					await deferDobPrompt(days);
					setShowPrompt(false);
				}}
			/>
			<BirthdayCelebrationOverlay
				open={showCelebration}
				name={currentUser?.name}
				onClose={() => setShowCelebration(false)}
			/>
		</>
	);
};

export default BirthdayExperienceManager;
